import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { ClipLoader } from 'react-spinners';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { FaLock, FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import LeaseApiService from '../../services/apiService';
import './leasedInstallmentsChange.css';

const apiService = new LeaseApiService();

const money = (v) =>
    v === null || v === undefined || v === ''
        ? ''
        : Number(v).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

// Dates arrive as ISO or "MM/DD/YYYY hh:mm:ss"; normalise to YYYY-MM-DD for comparisons, DD-MMM-YYYY for display.
const DATE_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY', moment.ISO_8601];
const dateKey = (v) => {
    if (!v) return '';
    const m = moment(String(v).split(' ')[0], DATE_FORMATS, true);
    return m.isValid() ? m.format('YYYY-MM-DD') : String(v);
};
const showDate = (v) => {
    const k = dateKey(v);
    return moment(k, 'YYYY-MM-DD', true).isValid() ? moment(k).format('DD-MMM-YYYY') : k;
};

const toOptions = (items) => (items || []).map((i) => ({ label: i.text, value: i.value }));

const errorMessage = (err) => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data) return data;
    return data?.message || err?.message || 'Something went wrong';
};

// explicit styles: the global hashed .css-xxxx-control rules only match some react-select states,
// which made the dropdowns change width after a selection
const selectProps = {
    className: 'p-0',
    menuPortalTarget: document.body,
    theme: (theme) => ({ ...theme, borderRadius: 5 }),
    styles: {
        container: (base) => ({ ...base, width: '100%', minWidth: 0 }),
        control: (base) => ({ ...base, width: '100%', height: 33, minHeight: 33 }),
        valueContainer: (base) => ({ ...base, minWidth: 0, padding: '0 8px' }),
        singleValue: (base) => ({ ...base, fontSize: 12, fontWeight: 400 }),
        placeholder: (base) => ({ ...base, fontSize: 12, fontWeight: 400 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base,
            fontSize: 12,
            color: '#000',
            backgroundColor: state.isFocused ? '#F5F5F5' : null,
            ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
        }),
    },
};

const emptyForm = { installment: '', firstDueDate: '', amount: '', vat: '', tol: '' };

const LeasedInstallmentsChange = () => {
    const [propertyList, setPropertyList] = useState([]);
    const [unitList, setUnitList] = useState([]);
    const [leaseList, setLeaseList] = useState([]);
    const [property, setProperty] = useState(null);
    const [unit, setUnit] = useState(null);
    const [lease, setLease] = useState(null);

    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null);
    const [loadedLeaseNo, setLoadedLeaseNo] = useState('');
    const [noData, setNoData] = useState(false);

    const [form, setForm] = useState(emptyForm);
    const [editAmounts, setEditAmounts] = useState(false);
    const [formError, setFormError] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [newSchedule, setNewSchedule] = useState(null);
    const errorRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiService.getApiParamater('GET_PROPERT_DROP', '?dropdownTypes=LInsChangePropertyName');
                setPropertyList(toOptions(res?.data?.dropdowns?.LInsChangePropertyName));
            } catch (err) {
                toast.error(errorMessage(err));
            }
        })();
    }, []);

    useEffect(() => {
        if (formError && errorRef.current) errorRef.current.focus();
    }, [formError]);

    const resetResult = () => {
        setDetails(null);
        setLoadedLeaseNo('');
        setNoData(false);
        setNewSchedule(null);
        setForm(emptyForm);
        setEditAmounts(false);
        setFormError('');
    };

    const handlePropertyChange = async (opt) => {
        setProperty(opt);
        setUnit(null);
        setLease(null);
        setUnitList([]);
        setLeaseList([]);
        resetResult();
        if (!opt) return;
        try {
            const params = new URLSearchParams();
            params.append('DropdownFor', 'LInsChangeUnitNoByPropId');
            params.append('CascadValue', opt.value);
            const res = await apiService.getApiParamater('GET_UNITNO_DROP', `?${params.toString()}`);
            setUnitList(toOptions(res?.data?.items));
        } catch (err) {
            toast.error(errorMessage(err));
        }
    };

    const handleUnitChange = async (opt) => {
        setUnit(opt);
        setLease(null);
        setLeaseList([]);
        resetResult();
        if (!opt) return;
        try {
            const params = new URLSearchParams();
            params.append('DropdownFor', 'LInsChangeLeaseNoByPropId');
            params.append('CascadValue', property?.value);
            params.append('CascadText', opt.label);
            const res = await apiService.getApiParamater('GET_UNITNO_DROP', `?${params.toString()}`);
            const options = toOptions(res?.data?.items);
            setLeaseList(options);
            // only one lease comes back for a unit; bind it straight away
            if (options.length > 0) setLease(options[0]);
        } catch (err) {
            toast.error(errorMessage(err));
        }
    };

    const seedForm = (remaining) => {
        setForm({
            installment: '',
            firstDueDate: dateKey(remaining?.firstDueDate),
            amount: remaining?.amount ?? '',
            vat: remaining?.vat ?? '',
            tol: remaining?.total ?? '',
        });
        setEditAmounts(false);
    };

    const loadLease = async () => {
        if (!lease?.value) return;
        setLoading(true);
        resetResult();
        try {
            const res = await apiService.getApiParamater('GET_LEASE_INVOICED_DETAILS', `?LeaseNo=${encodeURIComponent(lease.value)}`);
            const body = res?.data;
            if (res?.status === 204 || body?.statusCode === 204 || !body?.result) {
                setNoData(true);
            } else {
                setDetails(body.result);
                setLoadedLeaseNo(lease.value);
                seedForm(body.result.remaining);
            }
        } catch (err) {
            toast.error(errorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const remaining = details?.remaining;
    const invoiced = details?.invoicedInstallments || [];
    const totals = details?.invoicedTotals;
    const nothingLeft = !remaining || Number(remaining.amount) <= 0;
    const maxInstallments = Number(remaining?.remainingMonths) || 0;
    const invoicedMonths = new Set(invoiced.map((r) => dateKey(r.dueDate).slice(0, 7)).filter(Boolean));

    const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

    const validate = () => {
        const n = Number(form.installment);
        if (!Number.isInteger(n) || n < 1) return 'No. of installments must be at least 1';
        if (maxInstallments && n > maxInstallments) return `No. of installments cannot exceed remaining months (${maxInstallments})`;
        if (!form.firstDueDate) return 'First due date is required';
        const expiry = dateKey(remaining?.leaseExpiry);
        if (expiry && form.firstDueDate > expiry) return 'First due date cannot be after the lease expiry';
        if (invoicedMonths.has(form.firstDueDate.slice(0, 7))) return 'First due date overlaps an already invoiced installment';
        if (!(Number(form.amount) > 0)) return 'Amount must be greater than zero';
        return '';
    };

    const handleSaveClick = () => {
        const msg = validate();
        setFormError(msg);
        if (!msg) setShowConfirm(true);
    };

    const handleConfirmSave = async () => {
        setShowConfirm(false);
        setSaving(true);
        setFormError('');
        try {
            // no auto-retry: a timed-out save must be re-checked by the user
            const res = await apiService.post('SAVE_NEW_LEASE_INSTALLMENTS', {
                leaseNo: loadedLeaseNo,
                amount: Number(form.amount),
                vat: Number(form.vat) || 0,
                tol: Number(form.tol) || 0,
                installment: Number(form.installment),
                firstDueDate: form.firstDueDate,
            });
            toast.success(res?.data?.message || 'Installments saved');
            setNewSchedule(res?.data?.result || res?.data?.data || []);
            setForm((f) => ({ ...f, installment: '' }));
        } catch (err) {
            const status = err?.response?.status;
            if (status === 400) {
                setFormError(errorMessage(err));
            } else if (!err?.response) {
                setFormError('The request did not complete. Please reload the lease and re-check the schedule before saving again.');
            } else {
                toast.error(errorMessage(err));
            }
        } finally {
            setSaving(false);
        }
    };

    const perInstallment = Number(form.installment) > 0 && Number(form.amount) > 0 ? Number(form.amount) / Number(form.installment) : null;

    // client-side estimate only; the authoritative schedule comes back from the save
    const buildPreview = () => {
        const n = Number(form.installment);
        if (!remaining || !Number.isInteger(n) || n < 1 || !form.firstDueDate || !(Number(form.amount) > 0)) return [];
        const step = Math.max(1, Math.ceil((maxInstallments || n) / n));
        const expiry = dateKey(remaining.leaseExpiry) ? moment(dateKey(remaining.leaseExpiry)) : null;
        const split = (total) => {
            const each = Math.floor((Number(total) / n) * 1000) / 1000;
            return (i) => (i === n - 1 ? Number(total) - each * (n - 1) : each);
        };
        const amt = split(form.amount);
        const vat = split(form.vat || 0);
        const tot = split(form.tol || 0);
        return Array.from({ length: n }, (_, i) => {
            const start = moment(form.firstDueDate).add(i * step, 'months');
            let end = moment(start).add(step, 'months').subtract(1, 'day');
            if (expiry && end.isAfter(expiry)) end = expiry;
            return {
                key: i,
                desc: `Rent for the Period [${start.format('DD-MMM-YYYY')} - ${end.format('DD-MMM-YYYY')}]`,
                dueDate: start.format('DD-MMM-YYYY'),
                amount: amt(i),
                vat: vat(i),
                total: tot(i),
            };
        });
    };
    const preview = buildPreview();
    const previewTotals = preview.reduce((t, r) => ({ amount: t.amount + r.amount, vat: t.vat + r.vat, total: t.total + r.total }), { amount: 0, vat: 0, total: 0 });

    const quickPicks = remaining
        ? [
            ['Monthly', maxInstallments],
            ['Quarterly', Math.ceil(maxInstallments / 3)],
            ['Half-yearly', Math.ceil(maxInstallments / 6)],
            ['Single', 1],
        ].filter(([, n]) => n >= 1)
        : [];

    const handleClear = () => {
        setProperty(null);
        setUnit(null);
        setLease(null);
        setUnitList([]);
        setLeaseList([]);
        resetResult();
    };

    const num = { textAlign: 'right' };
    const Th = ({ children, right }) => <th scope="col" className={right ? 'lic-num' : ''}>{children}</th>;

    return (
        <div className="lic-page">
            <div className="lic-header px-4 py-3 d-flex align-items-center justify-content-between">
                <div>
                    <h1 className="lic-title">Change Lease Installments</h1>
                </div>
            </div>

            <div className="p-3 d-flex flex-column gap-3">
                {/* 1. Select lease */}
                <section className="lic-card">
                    <div className="lic-card-title"><span className="lic-step">1</span>Select Lease</div>
                    <div className="row g-3 align-items-end">
                        <div className="col-md-3">
                            <label className="form-label" htmlFor="lic-property">Property</label>
                            <Select {...selectProps} inputId="lic-property" options={propertyList} value={property} onChange={handlePropertyChange} placeholder="Select Property" isClearable />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label" htmlFor="lic-unit">Unit No</label>
                            <Select {...selectProps} inputId="lic-unit" options={unitList} value={unit} onChange={handleUnitChange} placeholder="Select Unit No" isDisabled={!property} isClearable />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label" htmlFor="lic-lease">Lease No</label>
                            <Select {...selectProps} inputId="lic-lease" options={leaseList} value={lease} onChange={(o) => { setLease(o); resetResult(); }} placeholder="Lease No" isDisabled={!unit} />
                        </div>
                        <div className="col-md-3 d-flex gap-2">
                            <button type="button" className="mainbtn flex-fill justify-content-center" onClick={loadLease} disabled={!lease?.value || loading}>
                                {loading ? <ClipLoader size={14} color="#fff" /> : 'Load Lease'}
                            </button>
                            <button type="button" className="mainbtnRed flex-fill justify-content-center" onClick={handleClear}>Reset</button>
                        </div>
                    </div>
                    {details && remaining && (
                        <div className="lic-info">
                            <div><div className="lic-info-label">Lease No</div><div className="lic-info-value">{loadedLeaseNo}</div></div>
                            <div><div className="lic-info-label">Lease Expiry</div><div className="lic-info-value">{showDate(remaining.leaseExpiry)}</div></div>
                            <div><div className="lic-info-label">Un-invoiced Installments</div><div className="lic-info-value">{remaining.currentNoOfInstallments} ({money(remaining.currentUnInvoicedTotal)})</div></div>
                            <div><div className="lic-info-label">Invoiced Amount / VAT / Total</div><div className="lic-info-value">{money(totals?.amount)} / {money(totals?.vat)} / {money(totals?.total)}</div></div>
                        </div>
                    )}
                    {noData && <div className="alert alert-info mt-3 mb-0">No installment details found for this lease.</div>}
                </section>

                {details && (
                    <>
                        {/* 2. Already invoiced */}
                        <section className="lic-card">
                            <div className="lic-card-title">
                                <span className="lic-step">2</span>Installments Already Invoiced
                                <span className="lic-pill"><FaLock className="me-1" />Locked — not changed</span>
                            </div>
                            <div className="lic-table-wrap">
                                <table className="lic-table">
                                    <thead>
                                        <tr>
                                            <Th>#</Th><Th>Description</Th><Th>Due Date</Th><Th>Invoice No</Th><Th>Invoice Date</Th>
                                            <Th right>Amount</Th><Th right>VAT</Th><Th right>ST</Th><Th right>Total</Th><Th right>Amount Paid</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiced.length === 0 && (
                                            <tr><td colSpan={10} className="text-center text-muted">No invoiced installments</td></tr>
                                        )}
                                        {invoiced.map((r, i) => (
                                            <tr key={r.record_Id}>
                                                <td>{i + 1}</td>
                                                <td>{r.installment_Desc}</td>
                                                <td>{showDate(r.dueDate)}</td>
                                                <td>{r.invoiceNo}</td>
                                                <td>{showDate(r.invoiceDate)}</td>
                                                <td className="lic-num">{money(r.amount)}</td>
                                                <td className="lic-num">{money(r.vat)}</td>
                                                <td className="lic-num">{money(r.st)}</td>
                                                <td className="lic-num">{money(r.total)}</td>
                                                <td className="lic-num">{money(r.actualAmountPaid)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    {totals && (
                                        <tfoot>
                                            <tr>
                                                <td colSpan={5}>Invoiced Total ({totals.noOfInstallments} installment{totals.noOfInstallments === 1 ? '' : 's'})</td>
                                                <td className="lic-num">{money(totals.amount)}</td>
                                                <td className="lic-num">{money(totals.vat)}</td>
                                                <td className="lic-num">{money(totals.st)}</td>
                                                <td className="lic-num">{money(totals.total)}</td>
                                                <td />
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </section>

                        {/* 3. Generate new installments */}
                        <section className="lic-card">
                            <div className="lic-card-title"><span className="lic-step">3</span>Generate New Installments</div>
                            {nothingLeft ? (
                                <div className="alert alert-warning mb-0">This lease is fully invoiced; there is nothing left to reschedule.</div>
                            ) : (
                                <>
                                    <div className="row g-3">
                                        <div className="col-lg-6">
                                            <div className="lic-ref">
                                                <div className="lic-ref-title">Amount to be scheduled (reference)</div>
                                                <div className="row g-2">
                                                    <div className="col-4">
                                                        <label className="form-label mb-0" htmlFor="lic-amount">Rent Amount</label>
                                                        {editAmounts
                                                            ? <input id="lic-amount" type="number" step="0.001" className="form-control" style={num} value={form.amount} onChange={(e) => setField('amount', e.target.value)} />
                                                            : <div className="lic-big">{money(form.amount)}</div>}
                                                    </div>
                                                    <div className="col-4">
                                                        <label className="form-label mb-0" htmlFor="lic-vat">VAT</label>
                                                        {editAmounts
                                                            ? <input id="lic-vat" type="number" step="0.001" className="form-control" style={num} value={form.vat} onChange={(e) => setField('vat', e.target.value)} />
                                                            : <div className="lic-big">{money(form.vat)}</div>}
                                                    </div>
                                                    <div className="col-4">
                                                        <label className="form-label mb-0" htmlFor="lic-total">Total</label>
                                                        {editAmounts
                                                            ? <input id="lic-total" type="number" step="0.001" className="form-control" style={num} value={form.tol} onChange={(e) => setField('tol', e.target.value)} />
                                                            : <div className="lic-big lic-accent">{money(form.tol)}</div>}
                                                    </div>
                                                </div>
                                                <div className="lic-sep" />
                                                <div className="row g-2">
                                                    <div className="col-4"><div className="lic-info-label">First un-invoiced due</div><div className="lic-info-value">{showDate(remaining.firstDueDate)}</div></div>
                                                    <div className="col-4"><div className="lic-info-label">Lease expiry</div><div className="lic-info-value">{showDate(remaining.leaseExpiry)}</div></div>
                                                    <div className="col-4"><div className="lic-info-label">Remaining months</div><div className="lic-info-value">{remaining.remainingMonths}</div></div>
                                                </div>
                                                <div className="lic-note mt-3">
                                                    {remaining.currentNoOfInstallments} un-invoiced installment{remaining.currentNoOfInstallments === 1 ? '' : 's'} will be replaced.
                                                </div>
                                                <div className="form-check mt-2">
                                                    <input id="lic-edit" type="checkbox" className="form-check-input" checked={editAmounts}
                                                        onChange={(e) => {
                                                            setEditAmounts(e.target.checked);
                                                            if (!e.target.checked) setForm((f) => ({ ...f, amount: remaining.amount, vat: remaining.vat, tol: remaining.total }));
                                                        }} />
                                                    <label className="form-check-label" htmlFor="lic-edit">Edit amounts</label>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-lg-6">
                                            <div className="lic-new">
                                                <div className="lic-new-title">New schedule</div>
                                                <div className="row g-3">
                                                    <div className="col-6">
                                                        <label className="form-label" htmlFor="lic-count">No of Installments</label>
                                                        <input id="lic-count" type="number" min={1} max={maxInstallments} step={1} className="form-control"
                                                            value={form.installment} onChange={(e) => setField('installment', e.target.value)} />
                                                    </div>
                                                    <div className="col-6">
                                                        <label className="form-label" htmlFor="lic-due">Due Start Date</label>
                                                        <input id="lic-due" type="date" className="form-control" max={dateKey(remaining.leaseExpiry) || undefined}
                                                            value={form.firstDueDate} onChange={(e) => setField('firstDueDate', e.target.value)} />
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                                                    <span className="lic-note">Quick pick:</span>
                                                    {quickPicks.map(([label, n]) => (
                                                        <button type="button" key={label} className={`lic-chip ${Number(form.installment) === n ? 'active' : ''}`}
                                                            onClick={() => setField('installment', String(n))}>{label} ({n})</button>
                                                    ))}
                                                </div>
                                                {formError && (
                                                    <div ref={errorRef} tabIndex={-1} role="alert" className="alert alert-danger mt-3 mb-0">{formError}</div>
                                                )}
                                                <button type="button" className="mainbtn w-100 justify-content-center mt-3" onClick={handleSaveClick} disabled={saving}>
                                                    {saving ? <ClipLoader size={14} color="#fff" /> : <><FaPlus />Generate Installments</>}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {!newSchedule && preview.length > 0 && (
                                        <div className="mt-3">
                                            <div className="d-flex justify-content-between align-items-end mb-2">
                                                <div className="fw-semibold">Preview — {preview.length} installment{preview.length === 1 ? '' : 's'}</div>
                                                <div className="lic-note">Estimate only · last installment absorbs rounding</div>
                                            </div>
                                            <div className="lic-table-wrap">
                                                <table className="lic-table">
                                                    <thead><tr><Th>#</Th><Th>Description</Th><Th>Due Date</Th><Th right>Amount</Th><Th right>VAT</Th><Th right>Total</Th></tr></thead>
                                                    <tbody>
                                                        {preview.map((r) => (
                                                            <tr key={r.key}>
                                                                <td>{r.key + 1}</td><td>{r.desc}</td><td>{r.dueDate}</td>
                                                                <td className="lic-num">{money(r.amount)}</td><td className="lic-num">{money(r.vat)}</td><td className="lic-num">{money(r.total)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td colSpan={3}>Total</td>
                                                            <td className="lic-num">{money(previewTotals.amount)}</td><td className="lic-num">{money(previewTotals.vat)}</td><td className="lic-num">{money(previewTotals.total)}</td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {newSchedule && (
                                <div className="mt-3">
                                    <div className="fw-semibold mb-2">Updated Schedule</div>
                                    <div className="lic-table-wrap">
                                        <table className="lic-table">
                                            <thead>
                                                <tr><Th>#</Th><Th>Description</Th><Th>Due Date</Th><Th right>Amount</Th><Th right>VAT</Th><Th right>ST</Th><Th right>Total</Th><Th>Invoice No</Th><Th>Status</Th></tr>
                                            </thead>
                                            <tbody>
                                                {newSchedule.map((r, i) => (
                                                    <tr key={r.record_Id}>
                                                        <td>{i + 1}</td>
                                                        <td>{r.installment_Desc}</td>
                                                        <td>{showDate(r.dueDate)}</td>
                                                        <td className="lic-num">{money(r.amount)}</td>
                                                        <td className="lic-num">{money(r.vat)}</td>
                                                        <td className="lic-num">{money(r.st)}</td>
                                                        <td className="lic-num">{money(r.total)}</td>
                                                        <td>{r.invoiceNo}</td>
                                                        <td>{r.isInvoiceGenerated
                                                            ? <span className="lic-badge-locked"><FaLock className="me-1" />Invoiced</span>
                                                            : <span className="lic-badge-new">New</span>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header closeButton><Modal.Title>Confirm</Modal.Title></Modal.Header>
                <Modal.Body>This will replace the current un-invoiced installments. Continue?</Modal.Body>
                <Modal.Footer>
                    <button type="button" className="mainbtnSecondary" onClick={() => setShowConfirm(false)}>Cancel</button>
                    <button type="button" className="mainbtn" autoFocus onClick={handleConfirmSave}>Continue</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LeasedInstallmentsChange;
